import React from "react";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./CustomClinikitActionButton.css";

/**
 * CustomClinikitActionButton
 * -------------------------------------------------------------
 * Polished, light action button used across the dashboard.
 * Features a soft mint gradient, an optional icon "chip", and
 * subtle micro-interactions that match the CliniKit design system.
 *
 * Usage:
 *   <CustomClinikitActionButton
 *     icon={<CalendarBlank size={16} weight="fill" />}
 *     onClick={handleClick}
 *   >
 *     הצג יומן קליניקה
 *   </CustomClinikitActionButton>
 *
 * Props:
 *   - children / label        Button text (children has priority).
 *   - icon                    React node rendered inside the icon chip.
 *                             Omit for a plain text button.
 *   - iconPosition            "start" (default) | "end" — RTL-aware.
 *   - variant                 "primary" (default) | "neutral" | "danger" |
 *                             "success" | "warning".
 *   - size                    "small" | "medium" (default) | "large".
 *   - fullWidth               Stretch button to container width.
 *   - loading                 Replace the icon with a spinner and disable.
 *   - disabled                Disables interactions + dims styling.
 *   - type                    Defaults to "button" (safe for forms).
 *   - onClick, title, className, ...rest — forwarded to <button>.
 */
function CustomClinikitActionButton({
  children,
  label,
  icon,
  iconPosition = "start",
  variant = "primary",
  size = "medium",
  fullWidth = false,
  loading = false,
  disabled = false,
  type = "button",
  onClick,
  className = "",
  title,
  ...rest
}) {
  const content = children ?? label;
  const isDisabled = disabled || loading;
  const hasIcon = Boolean(icon) || loading;
  const hasContent = Boolean(content);
  const iconAtEnd = iconPosition === "end";

  const classNames = [
    "ck-action-btn",
    `ck-action-btn--${variant}`,
    `ck-action-btn--${size}`,
    fullWidth && "ck-action-btn--full",
    iconAtEnd && "ck-action-btn--icon-end",
    hasIcon && !hasContent && "ck-action-btn--icon-only",
    !hasIcon && "ck-action-btn--no-icon",
    loading && "ck-action-btn--loading",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const iconNode = loading ? (
    <LoaderLoop size="xs" />
  ) : (
    icon
  );

  return (
    <button
      type={type}
      className={classNames}
      onClick={onClick}
      disabled={isDisabled}
      title={title}
      aria-busy={loading || undefined}
      {...rest}
    >
      {hasIcon && !iconAtEnd && (
        <span className="ck-action-btn__icon" aria-hidden="true">
          {iconNode}
        </span>
      )}
      {hasContent && <span className="ck-action-btn__label">{content}</span>}
      {hasIcon && iconAtEnd && (
        <span className="ck-action-btn__icon" aria-hidden="true">
          {iconNode}
        </span>
      )}
    </button>
  );
}

export default CustomClinikitActionButton;
